import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ScrollText, Loader2 } from "lucide-react";

const DISPLAY_OPTIONS = [
  { value: "name_and_location", label: "Show my name and location" },
  { value: "first_name", label: "Show first name only" },
  { value: "anonymous", label: "Show as Anonymous Truth Keeper" },
  { value: "hide_location", label: "Show name but hide location" },
  { value: "private", label: "Private registry only" },
];

export default function ClaimTruth() {
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", city: "", state: "", country: "",
    display_preference: "name_and_location",
  });

  useEffect(() => {
    http.get(`/claim/${code}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => setError(e.response?.data?.detail || "Claim code not found."));
  }, [code]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await http.post(`/claim/${code}`, form);
      setDone(data);
      toast.success("Truth registered.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Claim failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <section className="min-h-[60vh] bg-[#050505] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <ScrollText size={32} className="text-[#C9A055] mx-auto mb-4 opacity-70" />
            <h1 className="font-display text-3xl text-[#F4F1EA] mb-3">Claim code not found</h1>
            <p className="font-body text-[#8a857d]">
              The code <span className="text-[#C9A055] font-mono">{code}</span> isn&apos;t in our registry.
              Double-check the code on your certificate.
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!meta) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      </Layout>
    );
  }

  if (done) {
    return (
      <Layout>
        <section className="min-h-[80vh] bg-[#050505] flex items-center justify-center px-6 py-16">
          <div className="max-w-md text-center">
            <CheckCircle2 size={40} className="text-[#C9A055] mx-auto mb-6" />
            <h1 className="font-display text-4xl text-[#F4F1EA] leading-tight mb-4">
              Welcome to the Registry.
            </h1>
            <p className="font-body text-[16px] text-[#E8E4DC] leading-relaxed mb-2">
              Your truth <b>{meta.artwork_title}</b> is now officially registered.
            </p>
            <p className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055] mb-8">
              Truth Identifier · {done.truth_id}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/api/truths/${done.truth_id}/certificate.pdf`} target="_blank" rel="noreferrer"
                 className="btn-gold" data-testid="claim-cert">
                Download Certificate
              </a>
              <a href={`/truths/${done.truth_id}`} className="btn-outline" data-testid="claim-registry">
                View Registry Page
              </a>
            </div>
            <p className="font-body text-[12px] text-[#8a857d] italic mt-10">
              Every truth has a home.
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!meta.claimable) {
    return (
      <Layout>
        <section className="min-h-[60vh] bg-[#050505] flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <h1 className="font-display text-3xl text-[#F4F1EA] mb-3">Already registered</h1>
            <p className="font-body text-[#8a857d] mb-6">
              This truth has already been claimed by its owner.
            </p>
            <a href={`/truths/${meta.truth_id}`} className="btn-gold">View Registry Page</a>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-[#050505] py-16 sm:py-24 border-b border-white/5">
        <div className="max-w-xl mx-auto px-6 text-center">
          <ScrollText size={28} className="text-[#C9A055] mx-auto mb-5 opacity-70" />
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">
            Claim Your Truth
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F4F1EA] leading-tight mb-4"
              data-testid="claim-title">
            {meta.artwork_title}
          </h1>
          {meta.print_size && (
            <div className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d] mb-2">
              {meta.print_size}
            </div>
          )}
          <div className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055]">
            {meta.truth_id}
          </div>
        </div>
      </section>

      <section className="bg-[#050505] py-12">
        <form onSubmit={submit} className="max-w-xl mx-auto px-6 space-y-5" data-testid="claim-form">
          <Field label="Your name *" required>
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
                   data-testid="claim-name" className="stc-input" required />
          </Field>
          <Field label="Email *" required>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                   data-testid="claim-email" className="stc-input" required />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="City">
              <input value={form.city} onChange={(e) => set("city", e.target.value)}
                     data-testid="claim-city" className="stc-input" />
            </Field>
            <Field label="State">
              <input value={form.state} onChange={(e) => set("state", e.target.value)}
                     data-testid="claim-state" className="stc-input" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={(e) => set("country", e.target.value)}
                     data-testid="claim-country" className="stc-input" />
            </Field>
          </div>
          <Field label="Display preference">
            <select value={form.display_preference}
                    onChange={(e) => set("display_preference", e.target.value)}
                    data-testid="claim-display"
                    className="stc-input">
              {DISPLAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <button type="submit" disabled={submitting} data-testid="claim-submit"
                  className="btn-gold w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            Register This Truth
          </button>
          <p className="font-body text-[11px] text-[#8a857d] text-center italic">
            Every truth has a home.
          </p>
        </form>
      </section>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
